/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { IDropDownOption } from "@atg/icf-react-components/components/DropDownMenu/DropDownMenuOption";
import Select from "@atg/icf-react-components/components/Select";
import ITheme from "@atg/icf-react-components/themes/Theme";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";

import { JSS_FONT_FAMILY_SECONDARY, JSS_MEDIA_MOBILE_MAX, TranslationLang } from "~/constants";
import customColors from "~/utils/customColors";
import reportError from "~/utils/reportError";

const useStyles = createUseStyles({
  langSelector: {
    width: "auto",
    minWidth: "151px",
    [JSS_MEDIA_MOBILE_MAX]: {
      // eslint-disable-next-line @atg/css-rtl-support
      marginLeft: "48px",
    },
  },
});

// Different language for each option
const langOptions = [
  {
    label: "Deutsch",
    value: TranslationLang.DE,
  },
  {
    label: "English",
    value: TranslationLang.EN,
  },
  {
    label: "Español",
    value: TranslationLang.ES,
  },
  {
    label: "Français",
    value: TranslationLang.FR,
  },
  {
    label: "Italiano",
    value: TranslationLang.IT,
  },
  {
    label: "Português",
    value: TranslationLang.PT,
  },
  {
    label: "Română",
    value: TranslationLang.RO,
  },
  {
    label: "русский",
    value: TranslationLang.RU,
  },
];

const selectOverrides = {
  control: ({ theme }: { theme: ITheme }) => ({
    cursor: "pointer",
    borderRadius: 0,
    maxWidth: theme.spacing.base * 17,
    background: customColors.lightBlack,
    "&:focus, &:focus-visible": {
      borderColor: theme.colors.baseContrast040,
    },
    "&:hover": {
      borderColor: theme.colors.baseContrast040,
    },
  }),
  singleValue: () => ({
    fontFamily: JSS_FONT_FAMILY_SECONDARY,
    letterSpacing: 0.44,
  }),
  option: () => ({
    fontFamily: JSS_FONT_FAMILY_SECONDARY,
    letterSpacing: 0.44,
  }),
};

const LangSelector: FC = () => {
  const classes = useStyles();

  const { i18n } = useTranslation("footer");

  const defaultValue = langOptions.find((option) => String(option.value) === i18n.language) ?? langOptions[1];

  const onLangChange = (option: IDropDownOption) => {
    i18n.changeLanguage(option.value as string).catch(reportError);
  };

  return (
    <Select
      className={classes.langSelector}
      options={langOptions}
      onChange={(option) => onLangChange(option as IDropDownOption)}
      defaultValue={defaultValue}
      reactSelectOverrides={selectOverrides}
      isSearchable={false}
    />
  );
};

export default LangSelector;
